import * as React from "react";
import {translate} from "react-i18next";
import * as _ from "lodash";

// redux
import {connect} from "react-redux";

// router
import {RouteComponentProps} from "react-router-dom";

// services
import {
    MenuDto,
    PrivilegeDto,
    ResourceListOfPrivilegeDto,
} from "../../../services/Client";

// components
import Button from "../../../components/Button";

import PrivilegesTable from "./PrivilegesTable";
import TextField from "../../../components/TextField";
import {renderErrorToast} from "../../../utils/FeedbackMessageUtils";
import GeneralActions from "../../../redux/actions/GeneralActions";
import AdminViewActions from "../../../redux/actions/AdminViewActions";
import HATEOASClient from "../../../services/HATEOASClient";
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";
import {
    selectClient,
    selectAdminArealinks,
    selectPrivileges
} from "../../../redux/selectors";
import {compose, Dispatch} from "redux";
import {IReduxState} from "src/redux/IReduxState";

interface IMapStateToProps {
    adminAreaLinks?: MenuDto;
    client: HATEOASClient;
    privileges?: ResourceListOfPrivilegeDto;
}

interface IMapDispatchToProps {
    addTranslationLocation: () => void;
    receivePrivileges: (privileges: ResourceListOfPrivilegeDto) => void;
}

interface IOwnProps {

}

interface IPrivilegesViewProps extends IOwnProps, IMapStateToProps, IMapDispatchToProps, ITranslatedComponent, RouteComponentProps<any> {
}

interface IPrivilegesViewState {
    /** Whether or not we are waiting for a response from the back end. */
    loadingPrivileges: boolean;

    /** Privileges identifier filter */
    filter: string | undefined;

    /** Privileges that match the current filter */
    filteredPrivileges: PrivilegeDto[];
}

class PrivilegesView extends React.Component<IPrivilegesViewProps, IPrivilegesViewState> {
    constructor(props: IPrivilegesViewProps) {
        super(props);

        this.state = {
            loadingPrivileges: true,
            filter: undefined,
            filteredPrivileges: []
        };
    }

    componentDidMount() {
        this.props.addTranslationLocation();

        if (!this.props.privileges) {
            // we need a list of all available privileges for the roles table and creation dialog.
            this.requestPrivileges();
        }

        this.setState({
            loadingPrivileges: false,
            filter: undefined,
            filteredPrivileges: this.getPrivilegeDtos(this.props.privileges)
        });
    }

    requestPrivileges = (): void => {
        if (!this.props.adminAreaLinks ||
            !this.props.client.GET_privilegeList(this.props.adminAreaLinks).canExecute()) {
            return;
        }

        this.setState({loadingPrivileges: true});
        this.props.client.GET_privilegeList(this.props.adminAreaLinks).execute()
            .then((value: ResourceListOfPrivilegeDto) => {
                this.props.receivePrivileges(value);

                // request was successfully fulfilled; reset loading state
                this.setState({
                    loadingPrivileges: false,
                    filter: undefined,
                    filteredPrivileges: this.getPrivilegeDtos(this.props.privileges)
                });
            })
            .catch((reason: any) => {
                window.scrollTo(0, 0);
                renderErrorToast({
                    message: "ERRORS:GET_PRIVILEGES",
                    details: reason,
                    timestamp: Date.now()
                });
                this.setState({
                    loadingPrivileges: false
                });
            });
    }

    getPrivilegeDtos = (resOfPrivileges?: ResourceListOfPrivilegeDto): PrivilegeDto[] => {
        const dtos = resOfPrivileges && resOfPrivileges.elements && resOfPrivileges.elements;
        return dtos ? dtos : [];
    }

    renderTable = (loading: boolean, privileges: PrivilegeDto[]): JSX.Element => (
        <PrivilegesTable
            privileges={privileges}
            loading={loading}
        />
    )

    renderRefreshBtn = (loading: boolean): JSX.Element => (
        <Button
            label={this.props.t("RELOAD")}
            labelPosition="after"
            icon={"refresh"}
            disabled={loading}
            onClick={() => {
                this.requestPrivileges();
            }}
        />
    )

    applyFilter = (dtos: PrivilegeDto[], filter: string): PrivilegeDto[] => {
        const filterLowerCase = filter.toLowerCase();
        return dtos.filter(dto => _.includes(dto.identifier.toLowerCase(), filterLowerCase));
    }

    renderFilters = (): JSX.Element => (
        <TextField
            placeholder={this.props.t("FILTER_BY_IDENTIFIER")}
            value={this.state.filter}
            onChange={(event: React.FormEvent<{}>, newValue: string) => {
                const filterValue = newValue !== "" ? newValue : undefined;
                let filteredDtos = this.getPrivilegeDtos(this.props.privileges);
                if (filterValue) {
                    filteredDtos = this.applyFilter(filteredDtos, filterValue);
                }
                this.setState({
                    filter: filterValue,
                    filteredPrivileges: filteredDtos
                });
            }}
        />
    )

    render() {
        const filteredPrivileges = this.state.filteredPrivileges;
        return (
            <>
                {this.renderRefreshBtn(this.state.loadingPrivileges)}
                <br />
                {this.renderFilters()}
                {this.renderTable(this.state.loadingPrivileges, filteredPrivileges)}
            </>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    adminAreaLinks: selectAdminArealinks(state),
    client: selectClient(state),
    privileges: selectPrivileges(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>): IMapDispatchToProps => ({
    addTranslationLocation: () => dispatch(GeneralActions.AddTranslationLocation({pattern: "admin/privileges", substitutes: "Privileges Management"})),
    receivePrivileges: (privileges: ResourceListOfPrivilegeDto) => dispatch(AdminViewActions.ReceivePrivileges(privileges)),
});

export default compose<React.ComponentType<IOwnProps>>(
    translate("ADMIN_VIEW"),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(PrivilegesView);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/PrivilegesView.tsx